import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, Radio, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";

export default function Contact() {
  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
      <Seo
        title="Contact 1st Nations Security | 24/7 Ontario Security Dispatch"
        description="Contact 1st Nations Security for security guard services anywhere in Ontario. 24/7 dispatch: +1 807-770-0243 or admin@1stnation-security.com."
        path="/contact"
      />
      <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-3">Contact Us</p>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white font-heading mb-6">
        Contact 1st Nations Security
      </h1>
      <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-12">
        Need security guards, mobile patrol, fire watch or event security anywhere in Ontario? Reach our 24/7
        dispatch team directly by phone or email — no forms, no waiting. We respond to every enquiry personally.
      </p>

      <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mb-12">
        <a
          href="tel:+18077700243"
          data-testid="contact-phone-card"
          className="group rounded-2xl border border-amber-500/25 bg-slate-900/60 p-8 transition-all duration-200 hover:border-amber-500/50 hover:bg-slate-900"
        >
          <Phone className="h-10 w-10 text-amber-400 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Call Our 24/7 Dispatch</h2>
          <p className="text-2xl font-bold text-amber-400 mb-2 font-heading">+1 807-770-0243</p>
          <p className="text-sm text-slate-400 leading-relaxed">
            Day or night — for new security contracts, urgent guard coverage, fire watch or alarm response anywhere in Ontario.
          </p>
        </a>
        <a
          href="mailto:admin@1stnation-security.com"
          data-testid="contact-email-card"
          className="group rounded-2xl border border-amber-500/25 bg-slate-900/60 p-8 transition-all duration-200 hover:border-amber-500/50 hover:bg-slate-900"
        >
          <Mail className="h-10 w-10 text-amber-400 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Email Us Anytime</h2>
          <p className="text-lg font-bold text-amber-400 mb-2 break-all font-heading">admin@1stnation-security.com</p>
          <p className="text-sm text-slate-400 leading-relaxed">
            Send us your site details, coverage hours and location — we will reply with a security plan for your property or event.
          </p>
        </a>
      </div>

      <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mb-14">
        <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-6" data-testid="contact-hours-card">
          <Clock className="h-7 w-7 text-amber-400 mb-3" />
          <h3 className="text-base font-semibold text-white mb-1.5">Hours of Operation</h3>
          <p className="text-sm text-slate-400">24 hours a day, 7 days a week, 365 days a year. Our dispatch never closes.</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-6" data-testid="contact-coverage-card">
          <MapPin className="h-7 w-7 text-amber-400 mb-3" />
          <h3 className="text-base font-semibold text-white mb-1.5">Service Coverage</h3>
          <p className="text-sm text-slate-400">
            All of Ontario — headquartered in Northwestern Ontario (807), serving 45+ cities province-wide.{" "}
            <Link to="/ontario-service-areas" data-testid="contact-areas-link" className="text-amber-400 hover:text-amber-300">
              View service areas <ArrowRight className="inline h-3.5 w-3.5" />
            </Link>
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-6" data-testid="contact-dispatch-card">
          <Radio className="h-7 w-7 text-emerald-400 mb-3" />
          <h3 className="text-base font-semibold text-white mb-1.5">Emergency Coverage</h3>
          <p className="text-sm text-slate-400">
            Urgent fire watch, alarm response or same-day guard deployment? Call <a href="tel:+18077700243" className="text-amber-400 hover:text-amber-300">+1 807-770-0243</a> now.
          </p>
        </div>
      </div>

      <section data-testid="contact-services-note" className="max-w-3xl">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">
          Ontario Security Services Available on Request
        </h2>
        <p className="text-base text-slate-300 leading-relaxed">
          Uniformed security guards, mobile patrol security, construction site security, event security and crowd
          management, retail loss prevention, concierge and condominium security, industrial and mining security,
          healthcare security, 24/7 fire watch, alarm response, executive protection, Indigenous community security,
          K9 security and airport/transit security — all available with one call.{" "}
          <Link to="/security-services" data-testid="contact-services-link" className="text-amber-400 hover:text-amber-300 font-semibold">
            Browse all security services <ArrowRight className="inline h-4 w-4" />
          </Link>
        </p>
      </section>
    </div>
  );
}
